import * as Sentry from '@sentry/react';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import { Dedupe as DedupeIntegration } from '@sentry/integrations';
import { matchPath } from 'react-router-dom';
import initData from 'init-data';
import history from 'tradera-utils/history';
import { AxiosIntegration } from 'tradera-utils/sentry/axios-integration';
import { ALL_ROUTES } from 'tradera-constants/routes';

window.Sentry = Sentry;

const PRODUCTION_URL_REGEX = /(www|static)\.tradera\.(com|se|net)/;
const STAGING_URL_REGEX = /(beta-(a|b|c|verify)\.)tradera\.com/;
const DEVELOPMENT_URL_REGEXES = [
    /webpack-internal:\/\//,
    /localhost:\d+/,
    /127\.0\.0\.1:\d+/,
    /10\.\d+\.\d+\.\d+:\d+/,
    /192\.168.\.\d+\.\d+:\d+/,
    /((\w|-)+\.)+tradera\.(dev|service)/
];

const getEnvironment = () => {
    const origin = window.location.origin;
    if (PRODUCTION_URL_REGEX.test(origin)) {
        return 'production';
    }
    if (STAGING_URL_REGEX.test(origin)) {
        return 'staging';
    }
    if (DEVELOPMENT_URL_REGEXES.some(regex => regex.test(origin))) {
        return 'development';
    }
    if (process?.env?.NODE_ENV) {
        return process.env.NODE_ENV;
    }
    return 'unknown';
};

const DEBUG_SENTRY = window.location.search.includes('debug-sentry');

try {
    const environment = getEnvironment();
    // https://docs.sentry.io/error-reporting/configuration/?platform=javascript
    Sentry.init({
        dsn: 'https://aded37c82da24c40b403e8790998c27d@sentry.io/143263',
        environment,
        release: window.releaseVersion,
        // Sample rate is controlled by selective loading through the feature toggle "sentry"
        sampleRate: 1.0,
        tracesSampleRate: DEBUG_SENTRY ? 1 : 0.01,
        maxBreadcrumbs: 25,
        normalizeDepth: 5,
        integrations: [
            new TracingIntegrations.BrowserTracing({
                routingInstrumentation: Sentry.reactRouterV5Instrumentation(
                    history,
                    ALL_ROUTES,
                    matchPath
                )
            }),
            new DedupeIntegration(),
            new AxiosIntegration()
        ],
        beforeSend(event, hint) {
            console.error(
                hint.originalException || hint.syntheticException,
                event // Logs all data that would have been sent to Sentry (such as context & tags)
            );
            if (!DEBUG_SENTRY && environment === 'development') {
                // Do not send error to Sentry in development
                return null;
            }
            return event;
        },
        beforeBreadcrumb(breadcrumb, hint) {
            if (breadcrumb.category === 'ui.click') {
                const { target } = hint.event;
                if (target && target.ariaLabel) {
                    breadcrumb.message = target.ariaLabel;
                }
            }
            return breadcrumb;
        },
        allowUrls: [
            PRODUCTION_URL_REGEX,
            STAGING_URL_REGEX,
            ...DEVELOPMENT_URL_REGEXES
        ],
        denyUrls: [
            /chrome:\/\//i,
            /extensions\//i,
            /moz-extension:\/\//i,
            /graph\.facebook\.com/i,
            /connect\.facebook\.net\/en_US\/all\.js/i,
            /pixelsafe\.adsafeprotected.com/,
            /gstatic/,
            /gpt/i,
            /metrics\.itunes\.apple\.com\.edgesuite\.net/i,
            /cdn\.ampproject\.org/i,
            /adform\.net/i
        ],
        ignoreErrors: [
            'Loading chunk',
            'Loading CSS chunk',
            'TypeError: Failed to fetch',
            // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
            'ResizeObserver loop limit exceeded',
            // Google Translate extension: https://github.com/getsentry/sentry-javascript/issues/2418
            `TypeError: a[b].target.className.indexOf is not a function. (In 'a[b].target.className.indexOf(Zb)', 'a[b].target.className.indexOf' is undefined)`,
            'UET is not defined',
            'should_do_lastpass_here',
            // Error generated by a bug in SamsungBrowser/7.2
            'document.getElementsByClassName.ToString is not a function',
            `ReferenceError: Can't find variable: MoatMAK`
        ]
    });

    if (initData) {
        if (initData.isLoggedIn) {
            Sentry.setUser({
                id: initData.memberId
            });
        }
        Sentry.setTag('loggedIn', initData.isLoggedIn);
        Sentry.setTag('isHybridAppContext', initData.isHybridAppContext);
        Sentry.setTag('isNativeAppContext', initData.isNativeAppContext);
    }
} catch (e) {
    console.error(e);
}
